export const PUBLIC_GUIDE_MAX_IMAGE_BYTES = 10 * 1024 * 1024;

export const PUBLIC_GUIDE_BLOCK_TYPES = [
  { value: 'text', label: 'ข้อความ' },
  { value: 'image', label: 'รูปภาพ' },
  { value: 'youtube', label: 'YouTube' },
] as const;

export type PublicGuideBlockType = 'text' | 'image' | 'youtube';

export type PublicGuideKind = 'parking' | 'ev';

export const PUBLIC_GUIDE_KIND_OPTIONS: { value: PublicGuideKind; label: string }[] = [
  { value: 'parking', label: 'Parking' },
  { value: 'ev', label: 'EV Charger' },
];

export interface PublicGuideListItem {
  id: number;
  slug: string;
  guideKind: PublicGuideKind;
  titleTh: string;
  titleEn: string;
  summaryTh: string;
  coverImage: string;
  sortOrder: number;
  isPublished: boolean;
  isActive: boolean;
  blockCount: number;
}

export interface PublicGuideBlock {
  uiKey: string;
  id: number;
  sortOrder: number;
  blockType: PublicGuideBlockType;
  bodyTh: string;
  bodyEn: string;
  image: string;
  imagePath: string;
  imageExt: string;
  youtubeUrl: string;
  youtubeVideoId: string;
  isActive: boolean;
  pendingBase64: string | null;
  pendingFileName: string | null;
}

export interface PublicGuideDetail {
  id: number;
  slug: string;
  guideKind: PublicGuideKind;
  titleTh: string;
  titleEn: string;
  summaryTh: string;
  coverImage: string;
  coverImagePath: string;
  coverImageExt: string;
  sortOrder: number;
  isPublished: boolean;
  isActive: boolean;
  blocks: PublicGuideBlock[];
  coverPendingBase64: string | null;
  coverPendingFileName: string | null;
}

export interface PublicGuideTableResponse {
  total?: number;
  Total?: number;
  page?: number;
  Page?: number;
  pageSize?: number;
  PageSize?: number;
  items?: any[];
  Items?: any[];
}

export interface PublicGuideSavePayload {
  userId: number;
  menuId: number;
  id: number;
  slug: string;
  guideKind: PublicGuideKind;
  titleTh: string;
  titleEn?: string | null;
  summaryTh?: string | null;
  coverImageBase64?: string | null;
  coverImagePath?: string | null;
  coverImageExt?: string | null;
  sortOrder: number;
  isPublished: boolean;
  isActive: boolean;
  blocks: Array<{
    id: number;
    sortOrder: number;
    blockType: string;
    bodyTh?: string | null;
    bodyEn?: string | null;
    imageBase64?: string | null;
    imagePath?: string | null;
    imageExt?: string | null;
    youtubeUrl?: string | null;
    isActive: boolean;
  }>;
}

function newUiKey(): string {
  return `pg_${Date.now().toString(36)}_${Math.random().toString(36).slice(2, 8)}`;
}

export function createEmptyPublicGuideBlock(sortOrder = 10): PublicGuideBlock {
  return {
    uiKey: newUiKey(),
    id: 0,
    sortOrder,
    blockType: 'text',
    bodyTh: '',
    bodyEn: '',
    image: '',
    imagePath: '',
    imageExt: '',
    youtubeUrl: '',
    youtubeVideoId: '',
    isActive: true,
    pendingBase64: null,
    pendingFileName: null,
  };
}

export function createEmptyPublicGuide(): PublicGuideDetail {
  return {
    id: 0,
    slug: '',
    guideKind: 'parking',
    titleTh: '',
    titleEn: '',
    summaryTh: '',
    coverImage: '',
    coverImagePath: '',
    coverImageExt: '',
    sortOrder: 0,
    isPublished: false,
    isActive: true,
    blocks: [createEmptyPublicGuideBlock(10)],
    coverPendingBase64: null,
    coverPendingFileName: null,
  };
}

function normalizeGuideKind(raw: any): PublicGuideKind {
  const kind = String(raw ?? '').toLowerCase();
  return kind === 'ev' ? 'ev' : 'parking';
}

export function mapPublicGuideListItem(raw: any): PublicGuideListItem {
  return {
    id: Number(raw?.id ?? raw?.Id ?? 0),
    slug: String(raw?.slug ?? raw?.Slug ?? ''),
    guideKind: normalizeGuideKind(raw?.guideKind ?? raw?.GuideKind),
    titleTh: String(raw?.titleTh ?? raw?.TitleTh ?? ''),
    titleEn: String(raw?.titleEn ?? raw?.TitleEn ?? ''),
    summaryTh: String(raw?.summaryTh ?? raw?.SummaryTh ?? ''),
    coverImage: String(raw?.coverImage ?? raw?.CoverImage ?? ''),
    sortOrder: Number(raw?.sortOrder ?? raw?.SortOrder ?? 0),
    isPublished: Boolean(raw?.isPublished ?? raw?.IsPublished ?? false),
    isActive: Boolean(raw?.isActive ?? raw?.IsActive ?? true),
    blockCount: Number(raw?.blockCount ?? raw?.BlockCount ?? 0),
  };
}

export function mapPublicGuideBlock(raw: any): PublicGuideBlock {
  const type = String(raw?.blockType ?? raw?.BlockType ?? 'text').toLowerCase();
  return {
    uiKey: newUiKey(),
    id: Number(raw?.id ?? raw?.Id ?? 0),
    sortOrder: Number(raw?.sortOrder ?? raw?.SortOrder ?? 0),
    blockType: (type === 'image' || type === 'youtube' ? type : 'text') as PublicGuideBlockType,
    bodyTh: String(raw?.bodyTh ?? raw?.BodyTh ?? ''),
    bodyEn: String(raw?.bodyEn ?? raw?.BodyEn ?? ''),
    image: String(raw?.image ?? raw?.Image ?? ''),
    imagePath: String(raw?.imagePath ?? raw?.ImagePath ?? ''),
    imageExt: String(raw?.imageExt ?? raw?.ImageExt ?? ''),
    youtubeUrl: String(raw?.youtubeUrl ?? raw?.YoutubeUrl ?? ''),
    youtubeVideoId: String(raw?.youtubeVideoId ?? raw?.YoutubeVideoId ?? ''),
    isActive: Boolean(raw?.isActive ?? raw?.IsActive ?? true),
    pendingBase64: null,
    pendingFileName: null,
  };
}

export function mapPublicGuideDetail(raw: any): PublicGuideDetail {
  const blocksRaw = raw?.blocks ?? raw?.Blocks ?? [];
  return {
    id: Number(raw?.id ?? raw?.Id ?? 0),
    slug: String(raw?.slug ?? raw?.Slug ?? ''),
    guideKind: normalizeGuideKind(raw?.guideKind ?? raw?.GuideKind),
    titleTh: String(raw?.titleTh ?? raw?.TitleTh ?? ''),
    titleEn: String(raw?.titleEn ?? raw?.TitleEn ?? ''),
    summaryTh: String(raw?.summaryTh ?? raw?.SummaryTh ?? ''),
    coverImage: String(raw?.coverImage ?? raw?.CoverImage ?? ''),
    coverImagePath: String(raw?.coverImagePath ?? raw?.CoverImagePath ?? ''),
    coverImageExt: String(raw?.coverImageExt ?? raw?.CoverImageExt ?? ''),
    sortOrder: Number(raw?.sortOrder ?? raw?.SortOrder ?? 0),
    isPublished: Boolean(raw?.isPublished ?? raw?.IsPublished ?? false),
    isActive: Boolean(raw?.isActive ?? raw?.IsActive ?? true),
    blocks: Array.isArray(blocksRaw) ? blocksRaw.map(mapPublicGuideBlock) : [],
    coverPendingBase64: null,
    coverPendingFileName: null,
  };
}

export function slugifyTitle(title: string): string {
  return title
    .trim()
    .toLowerCase()
    .normalize('NFD')
    .replace(/[\u0300-\u036f]/g, '')
    .replace(/[^a-z0-9]+/g, '-')
    .replace(/^-+|-+$/g, '')
    .replace(/-{2,}/g, '-');
}

export function toPublicGuideSavePayload(
  guide: PublicGuideDetail,
  userId: number,
  menuId: number,
): PublicGuideSavePayload {
  return {
    userId,
    menuId,
    id: guide.id,
    slug: guide.slug.trim().toLowerCase(),
    guideKind: guide.guideKind === 'ev' ? 'ev' : 'parking',
    titleTh: guide.titleTh.trim(),
    titleEn: guide.titleEn?.trim() || null,
    summaryTh: guide.summaryTh?.trim() || null,
    coverImageBase64: guide.coverPendingBase64,
    coverImagePath: guide.coverImagePath || null,
    coverImageExt: guide.coverImageExt || null,
    sortOrder: guide.sortOrder,
    isPublished: guide.isPublished,
    isActive: guide.isActive,
    blocks: guide.blocks.map((b, i) => ({
      id: b.id,
      sortOrder: b.sortOrder > 0 ? b.sortOrder : (i + 1) * 10,
      blockType: b.blockType,
      bodyTh: b.bodyTh?.trim() || null,
      bodyEn: b.bodyEn?.trim() || null,
      imageBase64: b.pendingBase64,
      imagePath: b.imagePath || null,
      imageExt: b.imageExt || null,
      youtubeUrl: b.youtubeUrl?.trim() || null,
      isActive: b.isActive,
    })),
  };
}

export function coverPreviewSrc(guide: PublicGuideDetail): string {
  return guide.coverPendingBase64 || guide.coverImage || '';
}

export function blockPreviewSrc(block: PublicGuideBlock): string {
  return block.pendingBase64 || block.image || '';
}
